import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, AlertCircle, BadgeCheck, Users, Brain, Scale, Heart, Sparkles, Mail, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import ThemeToggle from '../components/ThemeToggle';
import { useBrandSettings } from '../context/BrandSettingsContext';
import apiService from '../services/api';
import BrandLogo from '../components/BrandLogo';

const AboutPage = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const { tagline, logos } = useBrandSettings();
  
  // Feature vote state
  const [voteEmail, setVoteEmail] = useState('');
  const [isVoting, setIsVoting] = useState(false);
  const [voteSuccess, setVoteSuccess] = useState(false);
  const [voteMessage, setVoteMessage] = useState('');
  const [voteCount, setVoteCount] = useState(0);

  useEffect(() => {
    // Load vote count
    loadVoteCount();
  }, []);

  const loadVoteCount = async () => {
    try {
      const result = await apiService.getFeatureVoteCount('verification_badge');
      setVoteCount(result.vote_count || 0);
    } catch (err) {
      console.error('Failed to load vote count');
    }
  };

  const handleVoteSubmit = async (e) => {
    e.preventDefault();
    if (!voteEmail) return;
    
    setIsVoting(true);
    try {
      const result = await apiService.voteForFeature(
        voteEmail, 
        'verification_badge', 
        'KaiCheckIt Verification Badge System'
      );
      setVoteSuccess(true);
      setVoteMessage(result.message);
      setVoteEmail('');
      loadVoteCount();
    } catch (err) {
      setVoteMessage('Failed to register interest. Please try again.');
    } finally {
      setIsVoting(false);
    }
  };

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mobile/Short version
  if (isMobile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
        {/* Header */}
        <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <BrandLogo size="sm" data-testid="brand-logo-mobile-header" />
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
                KaiCheckIt
              </span>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          <div className="max-w-lg mx-auto space-y-6">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-4">About KaiCheckIt</h1>
            </div>

            <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
              KaiCheckIt helps you pause before belief — and know when accountability exists behind content, even AI-generated content.
            </p>

            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              We help users check statements and images against trusted public sources, and clearly show what is supported, unclear, or unsupported by evidence.
            </p>

            {/* Coming Soon - Verification Badge */}
            <Card className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/30 dark:to-blue-900/30 border-purple-200 dark:border-purple-800 relative overflow-hidden">
              <div className="absolute top-2 right-2">
                <span className="inline-flex items-center gap-1 bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                  <Sparkles className="w-3 h-3" />
                  Coming Soon
                </span>
              </div>
              <CardContent className="pt-8 pb-5 space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <BadgeCheck className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  <p className="font-semibold text-slate-800 dark:text-slate-100">KaiCheckIt Verification Badge</p>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Soon, content creators — including those using AI avatars — will be able to display a verification badge showing:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <CheckCircle className="w-4 h-4 text-purple-500 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    A real human creator has been verified
                  </li>
                  <li className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <CheckCircle className="w-4 h-4 text-purple-500 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    Accountability exists behind the content
                  </li>
                  <li className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <CheckCircle className="w-4 h-4 text-purple-500 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    The creator has agreed to responsible standards
                  </li>
                </ul>
                
                {/* Email signup */}
                {voteSuccess ? (
                  <div className="bg-green-100 dark:bg-green-900/50 rounded-lg p-3 mt-4">
                    <p className="text-green-700 dark:text-green-300 text-sm text-center">{voteMessage}</p>
                  </div>
                ) : (
                  <form onSubmit={handleVoteSubmit} className="mt-4">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                      Interested? Leave your email to be notified when this launches:
                    </p>
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        value={voteEmail}
                        onChange={(e) => setVoteEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="flex-1 text-sm h-9 dark:bg-slate-800 dark:border-slate-600"
                        required
                      />
                      <Button 
                        type="submit" 
                        size="sm"
                        disabled={isVoting}
                        className="bg-purple-600 hover:bg-purple-700 h-9"
                      >
                        {isVoting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                      </Button>
                    </div>
                    {voteCount > 0 && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 text-center">
                        {voteCount} {voteCount === 1 ? 'person is' : 'people are'} interested in this feature
                      </p>
                    )}
                  </form>
                )}
              </CardContent>
            </Card>

            <div className="bg-slate-100 dark:bg-slate-800 rounded-xl p-4 text-center">
              <p className="text-slate-700 dark:text-slate-200 font-medium">
                KaiCheckIt is a trust layer — not an authority.
              </p>
            </div>

            <div className="text-center pt-6">
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-400 mb-1">
                KaiCheckIt
              </p>
              <p className="text-lg font-medium text-blue-600 dark:text-blue-300">
                {tagline}
              </p>
            </div>

            <div className="pt-8 space-y-3">
              <Button
                onClick={() => navigate('/')}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                Start Checking
              </Button>
              <p className="text-slate-500 dark:text-slate-400 text-xs text-center">
                Free to try • <a href="/pricing" className="text-blue-600 dark:text-blue-400 hover:underline">View Pro features</a>
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Full/Desktop version
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <BrandLogo size="md" data-testid="brand-logo-desktop-header" />
            <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt
            </span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-slate-800 dark:text-slate-100 mb-6">About KaiCheckIt</h1>
            <p className="text-xl text-slate-600 dark:text-slate-300 leading-relaxed max-w-3xl mx-auto">
              KaiCheckIt helps people pause before belief — and identify when accountability exists behind online content, including AI-generated content.
            </p>
          </div>

          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-16">
            <p className="text-slate-600 dark:text-slate-300 leading-relaxed text-lg">
              The internet is increasingly shaped by AI avatars, pseudonymous creators, and automated content. While anonymity and creativity have value, the absence of accountability has made it harder to know who — if anyone — stands behind what we see.
            </p>
            <p className="text-slate-700 dark:text-slate-200 font-medium text-lg mt-4">
              KaiCheckIt exists to address that gap.
            </p>
          </div>

          <Separator className="my-12 dark:bg-slate-700" />

          {/* What KaiCheckIt Does */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-6 flex items-center gap-3">
              <Brain className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              What KaiCheckIt Does
            </h2>
            <p className="text-slate-600 dark:text-slate-300 text-lg leading-relaxed mb-6">
              KaiCheckIt is a verification and trust tool designed to help people:
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-700 dark:text-blue-300 font-bold">1</span>
                    </div>
                    <p className="text-slate-700 dark:text-slate-200">Check statements and images before believing or sharing them</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-700 dark:text-blue-300 font-bold">2</span>
                    </div>
                    <p className="text-slate-700 dark:text-slate-200">Identify when a real, accountable human stands behind content — even when that content is delivered by an AI avatar</p>
                  </div>
                </CardContent>
              </Card>
            </div>
            <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
              We review statements and images against trusted public sources, including global health bodies, peer-reviewed research, and official guidance. Our outputs clearly distinguish between:
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-xl p-4 text-center">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400 mx-auto mb-2" />
                <p className="text-green-800 dark:text-green-200 font-medium">Information supported by evidence</p>
              </div>
              <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4 text-center">
                <AlertCircle className="w-8 h-8 text-yellow-600 dark:text-yellow-400 mx-auto mb-2" />
                <p className="text-yellow-800 dark:text-yellow-200 font-medium">Information that is unclear or incomplete</p>
              </div>
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl p-4 text-center">
                <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400 mx-auto mb-2" />
                <p className="text-red-800 dark:text-red-200 font-medium">Information that lacks reliable support</p>
              </div>
            </div>
            <p className="text-slate-700 dark:text-slate-200 font-medium mt-6 text-center">
              When certainty isn't possible, KaiCheckIt says so.
            </p>
          </section>

          <Separator className="my-12 dark:bg-slate-700" />

          {/* Verification Badge - Coming Soon */}
          <section className="mb-16">
            <div className="relative">
              <div className="absolute -top-4 right-0 z-10">
                <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg">
                  <Sparkles className="w-4 h-4" />
                  Coming Soon
                </span>
              </div>
              <h2 className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-6 flex items-center gap-3">
                <BadgeCheck className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                The KaiCheckIt Verification Badge
              </h2>
            </div>
            <p className="text-slate-600 dark:text-slate-300 text-lg leading-relaxed mb-6">
              We're developing a verification badge system for content creators — including those using AI avatars — to demonstrate accountability.
            </p>
            <Card className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/30 dark:to-blue-900/30 border-purple-200 dark:border-purple-800 mb-8">
              <CardContent className="pt-6">
                <p className="text-slate-700 dark:text-slate-200 font-medium mb-4">The KaiCheckIt Verification Badge will signal that:</p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-500 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 dark:text-slate-300">A real human creator has been verified behind the account or AI avatar</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-500 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 dark:text-slate-300">Identity and credentials (where applicable) have been checked by KaiCheckIt</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-500 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 dark:text-slate-300">The creator has agreed to responsible communication and transparency standards</span>
                  </li>
                </ul>
                
                {/* Email signup form */}
                <div className="mt-8 pt-6 border-t border-purple-200 dark:border-purple-700">
                  {voteSuccess ? (
                    <div className="bg-green-100 dark:bg-green-900/50 rounded-lg p-4 text-center">
                      <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400 mx-auto mb-2" />
                      <p className="text-green-700 dark:text-green-300">{voteMessage}</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-slate-700 dark:text-slate-200 font-medium mb-3">
                        Interested in this feature? Be the first to know when it launches:
                      </p>
                      <form onSubmit={handleVoteSubmit} className="flex gap-3 max-w-md">
                        <Input
                          type="email"
                          value={voteEmail}
                          onChange={(e) => setVoteEmail(e.target.value)}
                          placeholder="your@email.com"
                          className="flex-1 dark:bg-slate-800 dark:border-slate-600"
                          required
                          data-testid="feature-vote-email"
                        />
                        <Button 
                          type="submit" 
                          disabled={isVoting}
                          className="bg-purple-600 hover:bg-purple-700"
                          data-testid="feature-vote-submit"
                        >
                          {isVoting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Mail className="w-4 h-4 mr-2" />
                              Notify Me
                            </>
                          )}
                        </Button>
                      </form>
                      {voteCount > 0 && (
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-3">
                          Join {voteCount} {voteCount === 1 ? 'other person' : 'others'} waiting for this feature
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800">
              <CardContent className="pt-6">
                <p className="text-amber-800 dark:text-amber-200 font-medium mb-3">When launched, importantly:</p>
                <ul className="space-y-2 text-amber-900 dark:text-amber-100">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600 dark:text-amber-400">•</span>
                    The creator's real identity will not be disclosed publicly
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600 dark:text-amber-400">•</span>
                    KaiCheckIt does not endorse content or guarantee correctness
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600 dark:text-amber-400">•</span>
                    Verified creators will remain open to scrutiny, correction, and re-checking
                  </li>
                </ul>
                <p className="text-amber-800 dark:text-amber-200 font-semibold mt-4">
                  The badge will indicate accountability without exposure.
                </p>
              </CardContent>
            </Card>
          </section>

          <Separator className="my-12 dark:bg-slate-700" />

          {/* What Badge Will Mean */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-6 flex items-center gap-3">
              <Scale className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              What the Badge Will Mean — and Won't Mean
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-4">The KaiCheckIt Badge will mean:</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-green-900 dark:text-green-100">
                      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                      A real person stands behind the content
                    </li>
                    <li className="flex items-start gap-2 text-green-900 dark:text-green-100">
                      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                      The creator has opted into verification and accountability
                    </li>
                    <li className="flex items-start gap-2 text-green-900 dark:text-green-100">
                      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                      There is a process for review, correction, and revocation
                    </li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">The KaiCheckIt Badge does not mean:</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-slate-700 dark:text-slate-300">
                      <AlertCircle className="w-5 h-5 text-slate-500 dark:text-slate-400 mt-0.5 flex-shrink-0" />
                      Content is always correct
                    </li>
                    <li className="flex items-start gap-2 text-slate-700 dark:text-slate-300">
                      <AlertCircle className="w-5 h-5 text-slate-500 dark:text-slate-400 mt-0.5 flex-shrink-0" />
                      KaiCheckIt agrees with opinions expressed
                    </li>
                    <li className="flex items-start gap-2 text-slate-700 dark:text-slate-300">
                      <AlertCircle className="w-5 h-5 text-slate-500 dark:text-slate-400 mt-0.5 flex-shrink-0" />
                      The creator is immune from challenge or criticism
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
            <p className="text-slate-600 dark:text-slate-300 text-center mt-6 font-medium">
              Verification is revocable if standards are not maintained.
            </p>
          </section>

          <Separator className="my-12 dark:bg-slate-700" />

          {/* What KaiCheckIt Is */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-6 flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              What KaiCheckIt Is — and Isn't
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-blue-700 dark:text-blue-400 mb-4">KaiCheckIt is:</h3>
                <ul className="space-y-3 text-slate-700 dark:text-slate-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    A trust layer for human- and AI-generated content
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    A way to bring evidence and accountability into fast-moving online spaces
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    A signal that some creators are willing to be held to higher standards
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-400 mb-4">KaiCheckIt is not:</h3>
                <ul className="space-y-3 text-slate-600 dark:text-slate-400">
                  <li className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-slate-400 dark:text-slate-500 mt-0.5 flex-shrink-0" />
                    A debunking site or political referee
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-slate-400 dark:text-slate-500 mt-0.5 flex-shrink-0" />
                    A replacement for professionals, search engines, or personal judgment
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-slate-400 dark:text-slate-500 mt-0.5 flex-shrink-0" />
                    A system that reveals or exposes anonymous creators
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <Separator className="my-12 dark:bg-slate-700" />

          {/* Why This Matters */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-6 flex items-center gap-3">
              <Heart className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              Why This Matters
            </h2>
            <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl p-8 mb-6">
              <div className="space-y-2 text-lg text-slate-700 dark:text-slate-200 font-medium">
                <p>Search engines return results.</p>
                <p>Social platforms reward confidence.</p>
                <p>AI accelerates scale.</p>
              </div>
            </div>
            <p className="text-slate-600 dark:text-slate-300 text-lg leading-relaxed mb-4">
              KaiCheckIt focuses on process, evidence, and accountability — without forcing creators to sacrifice privacy or safety.
            </p>
            <p className="text-slate-700 dark:text-slate-200 text-lg font-medium">
              In a digital world where trust is fragile, we believe it should be earned, signaled, and reversible.
            </p>
          </section>

          <Separator className="my-12 dark:bg-slate-700" />

          {/* About Kai */}
          <section className="mb-16 text-center">
            <h2 className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-6">About "Kai"</h2>
            <div className="max-w-2xl mx-auto">
              <p className="text-slate-600 dark:text-slate-300 text-lg leading-relaxed mb-6">
                "Kai" represents the moment you pause — before belief hardens, before sharing, before certainty sets in.
              </p>
              <p className="text-slate-700 dark:text-slate-200 text-lg font-medium mb-8">
                KaiCheckIt is built around that pause.
              </p>
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl p-8">
                <p className="text-3xl font-bold text-blue-100 mb-2">
                  KaiCheckIt
                </p>
                <p className="text-xl font-medium">
                  {tagline}
                </p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <div className="text-center pt-8 space-y-4">
            <Button
              onClick={() => navigate('/')}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-lg px-8"
            >
              Start Verifying Content
            </Button>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              Free to try • <a href="/pricing" className="text-blue-600 dark:text-blue-400 hover:underline">View Pro features</a>
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md mt-16">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BrandLogo variant="plain" data-testid="brand-logo-footer" />
              <span className="text-slate-600 dark:text-slate-400">© 2025 Kaiventive Holdings</span>
            </div>
            <div className="flex gap-6">
              <a href="/privacy" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Privacy</a>
              <a href="/cookies" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Cookies</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
